import { Box, Typography, Chip } from '@mui/material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';
import AnimatedMetric from '../AnimatedMetric';
import StepSlide, { StatCard, SplitView, DemoBullets } from '../StepSlide';
import { retroColors, demoColors } from '../theme';
import {
    BoltOutlined,
    EvStationOutlined,
    GridOnOutlined,
    ShieldOutlined,
    MemoryOutlined,
    StorageOutlined,
    OfflineBoltOutlined,
    HubOutlined,
} from '@mui/icons-material';

/**
 * Step 1: "Energy Is Going Distributed"
 */
export function Step1_EnergyDistributed({ visible }) {
    const forces = [
        {
            icon: <MemoryOutlined />,
            label: 'AI Compute',
            stat: '20-50%',
            detail: 'Annual demand growth. 5-7 year interconnection queues.',
            color: demoColors.actPlatform,
        },
        {
            icon: <EvStationOutlined />,
            label: 'Electrification',
            stat: '2x',
            detail: 'EV fleets, heat pumps — load growing at the edge.',
            color: chartColors.ev,
        },
        {
            icon: <GridOnOutlined />,
            label: 'Grid Congestion',
            stat: '$64B',
            detail: 'In blocked projects. 2.5-year transformer lead times.',
            color: vortxColors.gold,
        },
        {
            icon: <ShieldOutlined />,
            label: 'Resilience',
            stat: '87%',
            detail: 'Of transmission projects never reach operation.',
            color: vortxColors.error,
        },
    ];

    return (
        <StepSlide
            title="Energy Is Going Distributed"
            subtitle="Four converging forces are pushing power to the grid edge."
            keyStat="1-30 MW at distribution voltage connects in 6-18 months. No transmission queue. No community opposition. The grid edge is where capacity comes online."
            visible={visible}
        >
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                gap: 3,
                mt: 2,
            }}>
                {forces.map((force, i) => (
                    <Box key={i} sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid rgba(178, 140, 80, 0.1)`,
                        transition: 'border-color 0.3s ease',
                        '&:hover': { borderColor: force.color },
                    }}>
                        <Box sx={{ color: force.color, mb: 1.5, fontSize: '2rem' }}>
                            {force.icon}
                        </Box>
                        <Chip
                            label={force.label}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.05)',
                                color: force.color,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                mb: 1.5,
                            }}
                        />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontWeight: 700,
                            fontSize: '2rem',
                            color: force.color,
                            lineHeight: 1,
                            mb: 0.5,
                        }}>
                            {force.stat}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.85rem',
                            color: retroColors.dimmed,
                            lineHeight: 1.4,
                        }}>
                            {force.detail}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '1.1rem',
                color: vortxColors.white,
                mt: 4,
                textAlign: 'center',
                fontWeight: 500,
            }}>
                All four forces push in the same direction: energy at the edge.
            </Typography>
        </StepSlide>
    );
}

/**
 * Step 2: "Every Distributed Site Needs a Brain"
 */
export function Step2_SiteNeedsBrain({ visible }) {
    const siteTypes = [
        { label: 'Powered Land', icon: <BoltOutlined />, desc: 'BESS + backup gen + fiber for AI compute' },
        { label: 'C&I Solar+Storage', icon: <OfflineBoltOutlined />, desc: 'Rooftop PV + battery for commercial buildings' },
        { label: 'Fleet Depot', icon: <EvStationOutlined />, desc: 'EV charging coordination for vehicle fleets' },
        { label: 'Cold Chain', icon: <StorageOutlined />, desc: 'Refrigeration + thermal storage scheduling' },
        { label: 'Microgrid', icon: <HubOutlined />, desc: 'Multi-source islanded or grid-tied systems' },
    ];

    return (
        <StepSlide
            title="Every Distributed Site Needs a Brain"
            keyStat="The distributed energy market doesn't need more hardware vendors. It needs a platform that makes every site intelligent."
            visible={visible}
        >
            {/* Site type spectrum */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 4,
                justifyContent: 'center',
            }}>
                {siteTypes.map((site, i) => (
                    <Box key={i} sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid rgba(178, 140, 80, 0.1)`,
                        textAlign: 'center',
                        minWidth: 150,
                        flex: '1 1 150px',
                        maxWidth: 200,
                        '&:hover': { borderColor: vortxColors.gold },
                    }}>
                        <Box sx={{ color: vortxColors.gold, mb: 1 }}>{site.icon}</Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: vortxColors.white,
                            mb: 0.5,
                        }}>
                            {site.label}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.75rem',
                            color: retroColors.muted,
                        }}>
                            {site.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* The problem */}
            <SplitView
                left={
                    <Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            color: vortxColors.white,
                            mb: 2,
                        }}>
                            The Same Problem
                        </Typography>
                        <DemoBullets items={[
                            'Multiple assets (batteries, solar, generators, EV chargers, HVAC)',
                            'One interconnection — shared capacity constraint',
                            'Competing revenue streams — RA, arbitrage, demand charges, frequency regulation',
                            '35,040 dispatch decisions per year',
                        ]} />
                    </Box>
                }
                right={
                    <Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            color: vortxColors.error,
                            mb: 2,
                        }}>
                            Today's Reality
                        </Typography>
                        <DemoBullets
                            items={[
                                'Rules-of-thumb dispatch',
                                'Spreadsheet sizing — no optimization',
                                'Manual reporting — no automation',
                                'No multi-asset coordination',
                                'Leaving 15-25% of revenue on the table',
                            ]}
                            color={vortxColors.error}
                        />
                    </Box>
                }
            />

            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '1.15rem',
                color: vortxColors.white,
                mt: 4,
                textAlign: 'center',
                fontWeight: 500,
            }}>
                What they all need: intelligence to design, control to operate, analytics to prove it.
            </Typography>
        </StepSlide>
    );
}
